import React, { useEffect, useReducer } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heading, Loading } from '@puppet/react-components';
import Breadcrumbs from '@components/Breadcrumbs';
import { getRepositories, getSources } from '@utils/api/cd4pe/vcs/actions';
import useWorkspaceDomain from '@hooks/useWorkspaceDomain';
import useWorkspaceName from '@hooks/useWorkspaceName';
import PageLayout from '@components/PageLayout';
import { LINKS } from 'src/routes';
import EditCodeProjectForm, {
  EditCodeProjectFormOnChange,
  EDIT_CODE_PROJECT_FORM_ACTION_TYPES,
  getActualValue,
} from 'src/codeDelivery/components/EditCodeProjectForm';
import { useGetControlRepoV1 } from '@services/cd4pe/controlRepos';
import Cd4peError from '@components/Cd4peError';
import { defaultEditControlRepoState, reducer } from './reducer';
import {
  EditControlRepoPayload,
  editControlRepoActions,
  editControlRepo,
} from './actions';

const EditControlRepo = () => {
  const [state, dispatch] = useReducer(reducer, defaultEditControlRepoState);
  const navigate = useNavigate();
  const { t } = useTranslation('codeDelivery');
  const workspaceName = useWorkspaceName();
  const workspaceId = useWorkspaceDomain();
  const { name: repoDisplayName } = useParams();
  const location = useLocation();

  useEffect(() => {
    if (workspaceId) getSources(workspaceId, dispatch);
  }, [workspaceId]);

  useEffect(() => {
    if (state.saveControlRepoSuccess) {
      const previousPage = location.state?.from;

      if (previousPage?.includes(repoDisplayName)) {
        navigate(
          LINKS.codeDelivery.viewRepository({
            path: { workspace: workspaceName, name: repoDisplayName },
          }),
          {
            state: {
              alertMessage: t(
                'editCodeProject.form.successMessage.controlRepo',
                {
                  controlRepoName: repoDisplayName,
                },
              ),
            },
          },
        );
      } else {
        navigate(
          LINKS.codeDelivery.listRepositories({
            path: { workspace: workspaceName },
          }),
          {
            state: {
              alertMessage: t(
                'editCodeProject.form.successMessage.controlRepo',
                {
                  controlRepoName: repoDisplayName,
                },
              ),
            },
          },
        );
      }
    }
  }, [
    workspaceName,
    state.saveControlRepoSuccess,
    navigate,
    location.state?.from,
    repoDisplayName,
    t,
  ]);

  const { data, isLoading, error } = useGetControlRepoV1({
    workspaceId,
    controlRepoName: repoDisplayName!,
  });

  const srcRepoName = data?.srcRepoName;

  const matchedRepo =
    getActualValue(state.repositories, srcRepoName!) ||
    getActualValue(state.repositories, repoDisplayName!);

  const breadcrumbs = [
    { displayName: `${workspaceName}` },
    {
      displayName: t('newControlRepo.breadcrumb.controlRepos'),
      linkDestination: LINKS.codeDelivery.listRepositories({
        path: { workspace: workspaceName },
      }),
    },
    {
      displayName: `${repoDisplayName}`,
      linkDestination: LINKS.codeDelivery.viewRepository({
        path: { workspace: workspaceName, name: repoDisplayName },
      }),
    },
    { displayName: 'Edit' },
  ];

  const onFormChange: EditCodeProjectFormOnChange = (fieldName, values) => {
    dispatch({ type: fieldName, payload: values[fieldName] || '' });

    switch (fieldName) {
      case EDIT_CODE_PROJECT_FORM_ACTION_TYPES.SELECTED_SOURCE: {
        const selectedSource = getActualValue(
          state.sources,
          values.SELECTED_SOURCE || '',
        );
        if (selectedSource) {
          getRepositories(workspaceId, dispatch, selectedSource, srcRepoName);
        }
        break;
      }
      default:
    }
  };

  const onClickEditControlRepo = async () => {
    dispatch(editControlRepoActions.editControlRepoStart());

    const controlRepoPayload: EditControlRepoPayload = {
      workspaceId,
      controlRepoName: repoDisplayName!,
      requestBody: {
        srcRepoProvider: matchedRepo!.provider,
        srcRepoDisplayOwner: matchedRepo!.ownerDisplayName || '',
        srcRepoOwner:
          matchedRepo!.organization ||
          matchedRepo!.user ||
          matchedRepo!.project ||
          '',
        srcRepoId:
          matchedRepo!.project ||
          matchedRepo!.user ||
          matchedRepo!.organization ||
          '',
      },
    };

    editControlRepo(dispatch, controlRepoPayload);
  };

  const getSourceDisplayName = (sourceActualValue: string) => {
    return state.sources
      .map((source) => {
        if (source.actualValue.id === sourceActualValue) {
          return source.actualValue.displayName;
        }
        return null;
      })
      .join('');
  };

  const matchingError =
    !matchedRepo && state.selectedSource && !state.getReposLoading
      ? t('editCodeProject.form.errorMessage.controlRepo', {
          controlRepoName: srcRepoName,
          displayName: getSourceDisplayName(state.selectedSource),
        })
      : null;

  let saveErrMsg: string | null = null;
  if (state.saveControlRepoError) {
    saveErrMsg =
      typeof state.saveControlRepoError === 'string'
        ? state.saveControlRepoError
        : state.saveControlRepoError.message;
  }

  const onClickCancel = () => {
    navigate(
      LINKS.codeDelivery.listRepositories({
        path: { workspace: workspaceName },
      }),
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return <Cd4peError error={error} />;
  }

  return (
    <PageLayout
      data-testid="edit-control-repo-view"
      className="edit-control-repo-view"
    >
      <PageLayout.Header>
        <PageLayout.Breadcrumbs>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </PageLayout.Breadcrumbs>
        <Heading>Edit {repoDisplayName}</Heading>
      </PageLayout.Header>
      <PageLayout.Content>
        <EditCodeProjectForm
          workspaceName={workspaceName}
          projectType="controlRepo"
          sources={state.sources}
          selectedSource={state.selectedSource}
          getSourcesLoading={state.getSourcesLoading}
          getSourcesError={state.getSourcesError}
          getReposLoading={state.getReposLoading}
          getReposError={state.getReposError || matchingError}
          name={srcRepoName}
          saveLoading={state.saveControlRepoLoading}
          saveError={saveErrMsg}
          webhookError={state.saveControlRepoWebhookError}
          onFormChange={onFormChange}
          submitCallback={onClickEditControlRepo}
          submitDisabled={matchedRepo === null}
          cancelCallback={onClickCancel}
          closeModal={onClickCancel}
        />
      </PageLayout.Content>
    </PageLayout>
  );
};

export default EditControlRepo;
